import { Observable, of } from 'rxjs';

import { TechnicalData } from '../entities/technicalData';
import { GridApplication } from '../entities/gridApplication';
import { CommunicationService } from 'src/app/services/communication.service';

import { Document } from '../../jurisdiction/jurisdiction.domain';
import { UrlSegments } from '../../jurisdiction/jurisdiction.constants';
import { ConnectionAddress } from '../entities/personal-data/connectionAddress';
import { SubscriberAddress } from '../entities/personal-data/subscriberAddress';
import { AddressAndPersonalData } from '../entities/personal-data/addressAndPersonalData';
import { map } from 'rxjs/operators';

type Urls = {
  technicalData: string;
  uploadDocument: string;
};

export abstract class GridApplicationService<GridApp extends GridApplication> {
  private readonly connectionObjectUrl: string = UrlSegments.SaveConnectionObject;
  private readonly businessPartnerUrl: string = UrlSegments.SaveBusinessPartner;
  private readonly invoiceAddressUrl: string = UrlSegments.SaveInvoiceAddress;

  constructor(protected communicationService: CommunicationService, protected urls: Urls) {}

  public application: GridApp;

  public getApplication(applicationId: number): Observable<GridApp> {
    return this.fetchApplication(applicationId).pipe(
      map(application => {
        this.application = application;

        return this.application;
      })
    );
  }

  public createTechnicalData(technicalData: TechnicalData): Observable<TechnicalData> {
    return this.communicationService.postData<TechnicalData>(this.urls.technicalData, technicalData);
  }

  public updateTechnicalData(technicalData: TechnicalData): Observable<TechnicalData> {
    return this.communicationService.updateData<TechnicalData>(this.urls.technicalData, technicalData);
  }

  public updateDocuments(documents: Document[], applicationId: string): Observable<any> {
    const formData = this.prepareDocuments(documents, applicationId);
    return this.communicationService.updateData(this.urls.uploadDocument, formData);
  }

  public uploadDocuments(documents: Document[], applicationId?: string): Observable<any> {
    const formData = this.prepareDocuments(documents, applicationId);
    return this.communicationService.postData(this.urls.uploadDocument, formData);
  }

  public deleteDocuments(documentId: number): Observable<any> {
    return this.communicationService.deleteData(`${UrlSegments.Application}/document?document_id=${documentId}`);
  }

  public deleteApplicationDocuments(documentId: number): Observable<any> {
    return this.communicationService.deleteData(`${UrlSegments.Application}/document?document_id=${documentId}`);
  }

  public createConnectionObject(connectionAddress: ConnectionAddress): Observable<any> {
    return this.communicationService.postData(this.connectionObjectUrl, connectionAddress);
  }

  public updateConnectionObject(connectionAddress: ConnectionAddress, id: number): Observable<any> {
    return this.communicationService.updateData(`${this.connectionObjectUrl}?connectionobject_id=${id}`, connectionAddress);
  }

  public createBussinessPartner(subscriberAddress: SubscriberAddress): Observable<any> {
    return this.communicationService.postData(this.businessPartnerUrl, subscriberAddress);
  }

  public updateBussinessPartner(subscriberAddress: SubscriberAddress, id: number): Observable<any> {
    if (!id) return of(null);
    return this.communicationService.updateData(`${this.businessPartnerUrl}?businesspartner_id=${id ?? 0}`, subscriberAddress);
  }

  public createInvoiceAddress(address: AddressAndPersonalData): Observable<any> {
    return this.communicationService.postData(this.invoiceAddressUrl, address);
  }

  public updateInvoiceAddress(address: AddressAndPersonalData, recipient_id: string): Observable<any> {
    return this.communicationService.updateData(`${this.invoiceAddressUrl}?recipient_id=${recipient_id}`, address);
  }

  public deleteInvoiceAddress(invoiceAddressId: number): Observable<any> {
    return this.communicationService.deleteData(`${this.invoiceAddressUrl}?id=${invoiceAddressId}`);
  }

  get gridApplication(): GridApp {
    return this.application;
  }

  protected abstract fetchApplication(applicationId: number): Observable<GridApp>;

  public abstract createPpOperatorAddress(address: AddressAndPersonalData): Observable<any>;

  public abstract updatePpOperatorAddress(address: AddressAndPersonalData): Observable<any>;

  public abstract deletePpOperatorAddress(operatorAddressId: number): Observable<any>;

  protected prepareDocuments(documents: Document[], applicationId?: string): FormData {
    const formData = new FormData();
    documents.forEach((document, index) => {
      formData.append(`documents[${index}].file`, document.file);
      formData.append(`documents[${index}].applicationId`, applicationId ?? document.application_id.toString());

      if (document.document_id) {
        formData.append(`documents[${index}].documentId`, document.document_id.toString());
      }
      if (document.photovoltaic_id) {
        formData.append(`documents[${index}].photovoltaicId`, document.photovoltaic_id.toString());
      }
      if (document.inverter_id) {
        formData.append(`documents[${index}].inverterId`, document.inverter_id.toString());
      }
      if (document.generator_id) {
        formData.append(`documents[${index}].generatorId`, document.generator_id.toString());
      }
      if (document.converter_id) {
        formData.append(`documents[${index}].converterId`, document.converter_id.toString());
      }
      if (document.power_plant_protection_id) {
        formData.append(`documents[${index}].powerPlantProtectionId`, document.power_plant_protection_id.toString());
      }
      if (document.integrated_protection_id) {
        formData.append(`documents[${index}].integratedProtectionId`, document.integrated_protection_id.toString());
      }

      formData.append(`documents[${index}].name`, document.name);
      formData.append(`documents[${index}].skDocumentType`, document.sk_document_type);
    });

    return formData;
  }
}
